import {
  provideRouter,
  RouterModule,
  Routes,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withRouterConfig,
} from "@angular/router";

import { redirectGuard } from "./core/guards/redirect.guard";
import { bookResolver } from "./pages/book/book.resolver";
import { newsResolver } from "./pages/news/news.resolver";
import { newsItemResolver } from "./pages/news/news-item.resolver";

import { seriesRedirectGuard } from "./core/guards/series-redirect.guard";
import { bookNameHtmlRedirectGuard } from "./core/guards/book-name-html-redirect.guard";
import { htmlRedirectGuard } from "./core/guards/html-redirect.guard";

import { SeriesListComponent } from "./pages/series/series-list/series-list.component";
import { homepageResolver } from "./pages/homepage/homepage.resolver";
import { HomepageComponent } from "./pages/homepage/homepage.component";
import { languageGuard } from "./core/guards/language.guard";
import { RedirectComponent } from "./components/redirect/redirect.component";
import { NoOpComponent } from "./components/no-op/no-op.component";
import { BookPageComponent } from "./pages/book/book-page.component";

const jovisRoutes: Routes = [
  {
    path: "",
    component: HomepageComponent,
    resolve: {
      modules: homepageResolver,
    },
  },
  {
    path: "ancillary-test",
    loadComponent: () =>
      import("./components/ancillary/ancillary.component").then(
        (m) => m.AncillaryComponent,
      ),
  },
  {
    path: "search",
    loadComponent: () =>
      import("./pages/search-page/search-page.component").then(
        (m) => m.SearchPageComponent,
      ),
  },
  {
    path: "search.html",
    component: NoOpComponent,
    canActivate: [htmlRedirectGuard],
    data: { redirectTo: "search" },
  }, // redirect from old site
  {
    path: "books",
    component: NoOpComponent,
    canActivate: [htmlRedirectGuard],
    data: { redirectTo: "search" },
    pathMatch: "full",
  }, // redirect from old site
  {
    path: "books.html",
    component: NoOpComponent,
    canActivate: [htmlRedirectGuard],
    data: { redirectTo: "search" },
  }, // redirect from old site
  {
    path: "books/:path",
    loadComponent: () =>
      import("./components/redirect/redirect.component").then(
        (m) => m.RedirectComponent,
      ),
    canActivate: [bookNameHtmlRedirectGuard],
  },
  {
    path: "books/:prefix/:path",
    loadComponent: () =>
      import("./components/redirect/redirect.component").then(
        (m) => m.RedirectComponent,
      ),
    canActivate: [bookNameHtmlRedirectGuard],
  },
  {
    path: "buecher",
    component: NoOpComponent,
    canActivate: [htmlRedirectGuard],
    pathMatch: "full",
    data: { redirectTo: "search" },
  },
  {
    path: "buecher.html",
    component: NoOpComponent,
    canActivate: [htmlRedirectGuard],
    data: { redirectTo: "search" },
  }, // redirect from old site
  {
    path: "buecher/:path",
    loadComponent: () =>
      import("./components/redirect/redirect.component").then(
        (m) => m.RedirectComponent,
      ),
    canActivate: [bookNameHtmlRedirectGuard],
  },
  {
    path: "buecher/:prefix/:path",
    loadComponent: () =>
      import("./components/redirect/redirect.component").then(
        (m) => m.RedirectComponent,
      ),
    canActivate: [bookNameHtmlRedirectGuard],
  },
  {
    path: "buecher/:prefix/:subprefix/:path",
    loadComponent: () =>
      import("./components/redirect/redirect.component").then(
        (m) => m.RedirectComponent,
      ),
    canActivate: [bookNameHtmlRedirectGuard],
  },
  {
    // redirect from old site
    path: "e-books",
    component: NoOpComponent,
    canActivate: [htmlRedirectGuard],
    data: { redirectTo: "search?tags=eBook" },
    pathMatch: "full",
  },
  {
    // redirect from old site
    path: "e-books.html",
    component: NoOpComponent,
    canActivate: [htmlRedirectGuard],
    data: { redirectTo: "search?tags=eBook" },
    pathMatch: "full",
  },
  {
    path: "news",
    loadComponent: () =>
      import("./pages/news/news-page/news.component").then(
        (m) => m.NewsComponent,
      ),
    resolve: {
      news: newsResolver,
    },
    runGuardsAndResolvers: "always",
  },
  {
    path: "news.html",
    component: NoOpComponent,
    canActivate: [htmlRedirectGuard],
    data: { redirectTo: "news" },
  },
  {
    path: "events/**",
    component: NoOpComponent,
    canActivate: [htmlRedirectGuard],
    data: { redirectTo: "news" },
  }, // redirect from old site
  {
    path: "events.html",
    component: NoOpComponent,
    canActivate: [htmlRedirectGuard],
    data: { redirectTo: "news" },
  }, // redirect from old site
  {
    path: "news/:id",
    loadComponent: () =>
      import("./pages/news/news-item.component").then(
        (m) => m.NewsItemComponent,
      ),
    resolve: {
      newsItem: newsItemResolver,
    },
  },
  {
    path: "series",
    loadComponent: () =>
      import("./components/redirect/redirect.component").then(
        (m) => m.RedirectComponent,
      ),
    canActivate: [seriesRedirectGuard],
  },
  {
    path: "series/:key",
    loadComponent: () =>
      import("./pages/series/series.component").then((m) => m.SeriesComponent),
  },
  {
    path: "collection/:key",
    loadComponent: () =>
      import("./pages/collection/collection.component").then(
        (m) => m.CollectionComponent,
      ),
  },
  {
    path: "collections",
    loadComponent: () =>
      import("./pages/collections/collections.component").then(
        (m) => m.CollectionsComponent,
      ),
  },
  {
    path: "book/:isbn",
    component: BookPageComponent,
    resolve: {
      book: bookResolver,
    },
    runGuardsAndResolvers: "always",
  },
  {
    path: "s",
    loadComponent: () =>
      import("./pages/homepage/homepage-series/homepage-series.component").then(
        (m) => m.HomepageSeriesComponent,
      ),
  },
  {
    path: "cart",
    loadComponent: () =>
      import("./pages/shopping-cart/cart/cart.component").then(
        (m) => m.CartComponent,
      ),
  },
  {
    path: "publisher",
    loadChildren: () =>
      import("./pages/publisher/publisher.module").then(
        (m) => m.PublisherModule,
      ),
  },
  {
    path: "verlag",
    loadChildren: () =>
      import("./pages/publisher/publisher.module").then(
        (m) => m.PublisherModule,
      ),
  },
  {
    path: "impressum",
    loadComponent: () =>
      import("./pages/three-columns/three-columns.component").then(
        (m) => m.ThreeColumnsComponent,
      ),
    data: { slug: "imprint" },
  },
  {
    path: "imprint",
    loadComponent: () =>
      import("./pages/three-columns/three-columns.component").then(
        (m) => m.ThreeColumnsComponent,
      ),
    data: { slug: "imprint" },
  },
  {
    path: "datenschutz",
    loadComponent: () =>
      import("./pages/three-columns/three-columns.component").then(
        (m) => m.ThreeColumnsComponent,
      ),
    data: { slug: "datenschutz" },
  },
  {
    path: "privacy-policy",
    loadComponent: () =>
      import("./pages/three-columns/three-columns.component").then(
        (m) => m.ThreeColumnsComponent,
      ),
    data: { slug: "privacy-policy" },
  },
  {
    path: "cookie-policy",
    loadComponent: () =>
      import("./pages/cookie-policy/cookie-policy.component").then(
        (m) => m.CookiePolicyComponent,
      ),
    data: { slug: "cookie-policy" },
  },
  {
    path: "cookie-richtlinien",
    loadComponent: () =>
      import("./pages/cookie-policy/cookie-policy.component").then(
        (m) => m.CookiePolicyComponent,
      ),
    data: { slug: "cookie-policy" },
  },
  {
    path: "terms-and-conditions",
    loadComponent: () =>
      import("./pages/three-columns/three-columns.component").then(
        (m) => m.ThreeColumnsComponent,
      ),
    data: { slug: "terms-conditions" },
  },
  {
    path: "agb",
    loadComponent: () =>
      import("./pages/three-columns/three-columns.component").then(
        (m) => m.ThreeColumnsComponent,
      ),
    data: { slug: "terms-conditions" },
  },
  {
    path: "**",
    component: NoOpComponent,
    canActivate: [htmlRedirectGuard],
    data: { redirectTo: "search" },
  },
];

const routes: Routes = [
  {
    path: "",
    pathMatch: "full",
    canActivate: [languageGuard],
    component: RedirectComponent,
  },
  {
    path: "en",
    canActivate: [languageGuard],
    children: jovisRoutes,
  },
  {
    path: "de",
    canActivate: [languageGuard],
    children: jovisRoutes,
  },
  {
    path: "**",
    canActivate: [redirectGuard],
    component: RedirectComponent,
  },
];

export const routerProvider = provideRouter(
  routes,
  withEnabledBlockingInitialNavigation(),
  withComponentInputBinding(),
  withInMemoryScrolling({
    anchorScrolling: "enabled",
    scrollPositionRestoration: "enabled",
  }),
  withRouterConfig({
    onSameUrlNavigation: "reload",
    paramsInheritanceStrategy: "always",
  }),
);
